import { useIntl } from "react-intl";

import Badge from "components/v2/Badge/Badge";
import { ReactComponent as PackageUpIcon } from "assets/images/iconsV2/package-up.svg";
import { ReactComponent as PackageCheckIcon } from "assets/images/iconsV2/package-check.svg";
import { ReactComponent as PlaneIcon } from "assets/images/iconsV2/plane.svg";
import { ReactComponent as CargoShipIcon } from "assets/images/iconsV2/cargo-ship.svg";
import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import type { ConsigneeValue } from "@RequestFormV2/defaults/consigneeDefaults";
import type { NotifyPartyValue } from "@RequestFormV2/defaults/notifyPartyDefaults";
import { StopType, StopAddressType } from "types/MultiStop";
import {
  formatContactLine,
  formatHoursTime,
  formatStopDate,
  formatStreetAddress,
  getStopPortLabel,
} from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/reviewScheduleFormatters";
import type { PartyReviewSyncKind } from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/partyReviewSyncKind";
import {
  resolveConsigneeReviewSyncKind,
  resolveNotifyReviewSyncKind,
} from "@RequestFormV2/components/ReviewPage/sections/ReviewSchedule/partyReviewSyncKind";

type PartyAddressFields = Pick<
  ConsigneeValue,
  | "address_type"
  | "address_name"
  | "address_street"
  | "address_street_line2"
  | "address_city"
  | "address_state"
  | "address_zipcode"
  | "address_country"
  | "airport"
  | "airport_name"
  | "seaport"
  | "seaport_name"
>;

function formatPartyStreetAddress(party: PartyAddressFields): string {
  const parts: string[] = [];
  if (party.address_street) parts.push(party.address_street);
  if (party.address_street_line2) parts.push(party.address_street_line2);
  if (party.address_city) parts.push(party.address_city);
  if (party.address_state) parts.push(party.address_state);
  if (party.address_zipcode) parts.push(party.address_zipcode);
  if (party.address_country) parts.push(party.address_country);
  return parts.join(", ");
}

function formatPartyPortLabel(party: PartyAddressFields): string {
  if (party.address_type === "airport") {
    return `${party.airport || ""} - ${party.airport_name || ""}`.replace(/^- | -$/g, "").trim();
  }
  if (party.address_type === "seaport") {
    return `${party.seaport || ""} - ${party.seaport_name || ""}`.replace(/^- | -$/g, "").trim();
  }
  return "";
}

type PartyDisplay = {
  value: string;
  portLabel?: string;
  addressName?: string;
  streetAddress?: string;
  subtext?: string;
};

function getCustomPartyDisplay(
  party: PartyAddressFields & { contact_name: string; contact_phone: string; contact_email: string },
): PartyDisplay {
  const portLabel = formatPartyPortLabel(party);
  const streetAddress = formatPartyStreetAddress(party);

  return {
    value: party.contact_name || "-",
    portLabel: portLabel || undefined,
    addressName: party.address_name || undefined,
    streetAddress: streetAddress || undefined,
    subtext: formatContactLine(party.contact_phone, party.contact_email) || undefined,
  };
}

function hasConsigneeData(consignee?: ConsigneeValue): boolean {
  if (!consignee) return false;
  return !!(
    consignee.same_as_delivery_address_contact ||
    consignee.form_address_contact_type === "delivery" ||
    consignee.contact_name ||
    consignee.address_street ||
    consignee.airport_name ||
    consignee.seaport_name
  );
}

function hasNotifyPartyData(notifyParty?: NotifyPartyValue): boolean {
  if (!notifyParty) return false;
  return !!(
    notifyParty.same_as_address_contact !== "customAddressContact" ||
    notifyParty.contact_name ||
    notifyParty.address_street ||
    notifyParty.airport_name ||
    notifyParty.seaport_name
  );
}

interface StopRowProps {
  stop: ScheduleStopValue;
  getTimezoneLabel: (value?: string | null) => string;
}

export function StopRow({ stop, getTimezoneLabel }: StopRowProps) {
  const intl = useIntl();
  const isPickup = stop.stop_type === StopType.PICKUP;

  const dateText = formatStopDate(stop, intl, getTimezoneLabel);
  const streetAddress = formatStreetAddress(stop);
  const contactLine = formatContactLine(stop.stop_contact_number, stop.stop_contact_email);

  const isAirport = stop.stop_address_type === StopAddressType.AIRPORT;
  const isSeaport = stop.stop_address_type === StopAddressType.SEAPORT;

  const portLabel = getStopPortLabel(stop);

  return (
    <div className="rfv2-review__stop-row">
      <div className="rfv2-review__stop-location">
        <div className="rfv2-review__stop-top">
          <Badge
            label={
              isPickup
                ? intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" })
                : intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" })
            }
            color={isPickup ? "indigo" : "success"}
            type="category-badge"
            variant="icon-leading"
            size="sm"
            icon={isPickup ? <PackageUpIcon /> : <PackageCheckIcon />}
          />
          {dateText && <span className="rfv2-review__stop-date">{dateText}</span>}
        </div>

        <div className="rfv2-review__stop-address">
          {(isAirport || isSeaport) && portLabel && (
            <div className="rfv2-review__stop-address-type">
              <Badge
                label={
                  isAirport
                    ? intl.formatMessage({ id: "request-form.review.schedule.airport", defaultMessage: "Airport" })
                    : intl.formatMessage({ id: "request-form.review.schedule.port", defaultMessage: "Port" })
                }
                color="gray"
                type="status-badge"
                variant="icon-leading"
                size="sm"
                icon={isAirport ? <PlaneIcon width={16} height={16} /> : <CargoShipIcon width={16} height={16} />}
              />
              <span className="rfv2-review__field-value">{portLabel}</span>
            </div>
          )}
          {stop.address_name && <div className="rfv2-review__field-value">{stop.address_name}</div>}
          {streetAddress && <div className="rfv2-review__field-value">{streetAddress}</div>}
        </div>

        {(stop.stop_notes || (stop.stop_hours_start && stop.stop_hours_end)) && (
          <div className="rfv2-review__stop-instructions">
            {stop.stop_hours_start && stop.stop_hours_end && (
              <div className="rfv2-review__stop-meta">
                {intl.formatMessage(
                  { id: "request-form.review.schedule.hours", defaultMessage: "Hours: {start} - {end}" },
                  { start: formatHoursTime(stop.stop_hours_start), end: formatHoursTime(stop.stop_hours_end) },
                )}
              </div>
            )}
            {stop.stop_notes && (
              <div className="rfv2-review__stop-meta">
                {intl.formatMessage(
                  { id: "request-form.review.schedule.instructions", defaultMessage: "Instructions: {value}" },
                  { value: stop.stop_notes },
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rfv2-review__stop-contact">
        <span className="rfv2-review__field-label">
          {intl.formatMessage({ id: "request-form.review.schedule.contact", defaultMessage: "Contact" })}
        </span>
        <span className="rfv2-review__field-value">{stop.stop_contact_name || "-"}</span>
        {contactLine && <span className="rfv2-review__stop-meta">{contactLine}</span>}
      </div>
    </div>
  );
}

function PartyRow({
  label,
  party,
  syncKind,
}: {
  label: string;
  party: PartyAddressFields & { contact_name: string; contact_phone: string; contact_email: string };
  syncKind: PartyReviewSyncKind | null;
}) {
  const intl = useIntl();
  if (syncKind) {
    const syncMessage =
      syncKind === "delivery"
        ? intl.formatMessage({
            id: "request-form.review.schedule.sameAsDelivery",
            defaultMessage: "Same as delivery address and contact",
          })
        : intl.formatMessage({
            id: "new-request.schedule.sameAsConsigneeAddressContact",
            defaultMessage: "Same as consignee address & consignee contact",
          });

    return (
      <div className="rfv2-review__stop-row">
        <div className="rfv2-review__stop-location">
          <div className="rfv2-review__stop-top">
            <span className="rfv2-review__field-label">{label}</span>
          </div>
          <span className="rfv2-review__field-value">{syncMessage}</span>
        </div>
      </div>
    );
  }

  const display = getCustomPartyDisplay(party);
  const isAirport = party.address_type === "airport";
  const isSeaport = party.address_type === "seaport";

  return (
    <div className="rfv2-review__stop-row">
      <div className="rfv2-review__stop-location">
        <div className="rfv2-review__stop-top">
          <span className="rfv2-review__field-label">{label}</span>
        </div>

        <div className="rfv2-review__stop-address">
          {(isAirport || isSeaport) && display.portLabel && (
            <div className="rfv2-review__stop-address-type">
              <Badge
                label={
                  isAirport
                    ? intl.formatMessage({ id: "request-form.review.schedule.airport", defaultMessage: "Airport" })
                    : intl.formatMessage({ id: "request-form.review.schedule.port", defaultMessage: "Port" })
                }
                color="gray"
                type="status-badge"
                variant="icon-leading"
                size="sm"
                icon={isAirport ? <PlaneIcon width={16} height={16} /> : <CargoShipIcon width={16} height={16} />}
              />
              <span className="rfv2-review__field-value">{display.portLabel}</span>
            </div>
          )}
          {display.addressName && <div className="rfv2-review__field-value">{display.addressName}</div>}
          {display.streetAddress && <div className="rfv2-review__field-value">{display.streetAddress}</div>}
        </div>
      </div>

      <div className="rfv2-review__stop-contact">
        <span className="rfv2-review__field-label">
          {intl.formatMessage({ id: "request-form.review.schedule.contact", defaultMessage: "Contact" })}
        </span>
        <span className="rfv2-review__field-value">{display.value}</span>
        {display.subtext && <span className="rfv2-review__stop-meta">{display.subtext}</span>}
      </div>
    </div>
  );
}

export function ConsigneeNotifyRow({
  consignee,
  notifyParty,
  deliveryStop,
}: {
  consignee?: ConsigneeValue;
  notifyParty?: NotifyPartyValue;
  deliveryStop?: ScheduleStopValue;
}) {
  const intl = useIntl();

  const showConsignee = hasConsigneeData(consignee);
  const showNotifyParty = hasNotifyPartyData(notifyParty);

  if (!showConsignee && !showNotifyParty) return null;

  return (
    <>
      {showConsignee && consignee && (
        <PartyRow
          label={intl.formatMessage({ id: "request-form.review.schedule.consignee", defaultMessage: "Consignee" })}
          party={consignee}
          syncKind={resolveConsigneeReviewSyncKind(consignee, deliveryStop)}
        />
      )}
      {showNotifyParty && notifyParty && (
        <PartyRow
          label={intl.formatMessage({ id: "request-form.review.schedule.notifyParty", defaultMessage: "Notify party" })}
          party={notifyParty}
          syncKind={resolveNotifyReviewSyncKind(notifyParty, consignee, deliveryStop)}
        />
      )}
    </>
  );
}
